import { EnumsState } from '@/types/store/Enums'
import { ActionContext } from "vuex"
import axios, { AxiosInstance } from 'axios'
import { Enum, EnumResource } from '@/types/Enum'

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_DATA_API_URL,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
})

type Context = ActionContext<EnumsState, {}>
type SetEnumsPayload = {
  types: Enum['type'][]
  enums: EnumResource[]
}

// initial state
const state = () => <EnumsState>({
  all: [],
})

// getters
const getters = {
  all: (state: EnumsState): Enum[] => state.all,
  findById: (state: EnumsState) => (id: Enum['id'], type?: Enum['type']): Enum | undefined => {
    if (type) return state.all.find((item: Enum) => item.id === id && item.type === type)
    return state.all.find((item: Enum) => item.id === id)
  },
  findByType: (state: EnumsState) => (type: Enum['type']): Enum[] => state.all.filter((item: Enum) => item.type === type),
}

// actions
const actions = {
  async fetchAll(context: Context, types: Enum['type'][]): Promise<void> {
    const requestedTypes = [...new Set(
      types
        .map((type) => type.trim())
        .filter(Boolean)
    )]

    if (!requestedTypes.length) {
      console.error('At least one enum type is required')
      return
    }

    try {
      const token = context.rootGetters['auth/JWTToken']
      const { data } = await api.get('enums/fetchall', {
        params: {
          types: requestedTypes.join(',')
        },
        headers: token ? {
          Authorization: `Bearer ${token}`
        } : undefined
      })

      context.commit('set', {
        types: requestedTypes,
        enums: data.Data
      })
    } catch (error: any) {
      console.error(error)
    }
  }
}

// mutations
const mutations = {
  set(state: EnumsState, payload: SetEnumsPayload): void {
    const requestedTypes = new Set(payload.types.map((type) => type.toLowerCase()))
    const enums = payload.enums.map((item: EnumResource) => {
      // Remove Cosmos DB metadata
      const {
        _attachments, _etag, _rid, _self, _ts,
        ...rest
      } = item
      return rest
    })

    state.all = [
      ...state.all.filter((item) => !requestedTypes.has(item.type.toLowerCase())),
      ...enums
    ]
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
